<template>
  <div>
    <McButton
      v-if="hasAccess(['share-report:read'])"
      @click="showModal = !showModal"
      variant="secondary"
      :text="$t('liveView.infoWindow.share.text')"
      icon="icon-outline_share"
    />
    <b-modal
      v-model="showModal"
      scrollable
      size="xl"
      class="mc-modal"
      :title="$t('report.object.shareReport') + ` - ${report.labelName ?? $t(report.label)}`"
    >
      <Contact
        :title="$t('report.title.determineRecipients')"
        :fields="fields"
        :hasData="hasData"
        :hasEmptyState="false"
        :initialItems="formValue.employeesList"
        @customTag="addTag"
        @updateCountryCode="updateCountryCode"
        @updateData="updateData"
      >
        <template #description>
          <span class="mb-2">{{ $t('report.text.descContactReport') }}</span>
        </template>
        <template #invalid-feedback>
          <span v-if="isInvalidContact && isSubmit" class="text-danger text-sm-italic">{{
            $t('global.validation.required')
          }}</span>
        </template>
      </Contact>
      <b-row class="align-items-center">
        <b-col md="3">
          <span class="required">{{ $t('report.object.chooseFormat') }}</span>
          <span v-b-tooltip.topleft :title="$t('report.object.shareReportCsv')" class="ms-2">
            <i class="icon-outline_info"></i>
          </span>
        </b-col>
        <b-col md="3">
          <multiselect
            track-by="value"
            label="label"
            v-model="formValue.downloadAs"
            :options="downloadOptions"
            :show-labels="false"
          >
            <template #singleLabel="props">
              <i :class="`option__image pe-2 mc-fa ${props.option.icon}`"></i>
              <span class="option__title">{{ props.option.label }}</span>
            </template>
            <template #option="props">
              <i :class="`option__image pe-2 mc-fa ${props.option.icon}`"></i>
              <span class="option__title">{{ props.option.label }}</span>
            </template>
          </multiselect>
        </b-col>
      </b-row>
      <b-row v-if="!stateDownloadAs">
        <b-col md="3" />
        <b-col md="3">
          <span class="text-danger text-sm-italic">{{ $t('global.validation.required') }}</span>
        </b-col>
      </b-row>

      <b-row
        class="mt-3 mb-2"
        v-if="hasAccessAll(['share-report:create', 'a5a-penjadwalan-report:read'])"
      >
        <b-col>
          <div class="form-check">
            <b-form-checkbox
              v-model="formValue.isSchedule"
              :value="true"
              :unchecked-value="false"
              @change="toggleSchedule"
            >
              {{ $t('report.object.scheduleReportDesc') }}
            </b-form-checkbox>
          </div>
        </b-col>
      </b-row>

      <div v-if="formValue.isSchedule">
        <b-row class="align-items-center">
          <b-col md="3">
            <span class="required">{{ $t('report.title.scheduleName') }}</span>
          </b-col>
          <b-col md="3">
            <b-form-group
              label-for="scheduler-name"
              :invalid-feedback="invalidSchedulerName"
              :state="stateSchedulerName"
            >
              <b-form-input id="scheduler-name" v-model="formValue.scheduleName" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="align-items-center my-3">
          <b-col md="3">
            <b-form-radio v-model="formValue.period" value="d">
              <label class="radio-label">{{ $t('report.object.everyDay') }}</label>
            </b-form-radio>
          </b-col>
          <b-col md="3">
            <date-picker
              v-model="formValue.time"
              format="HH:mm"
              value-type="format"
              type="time"
              placeholder="HH:mm"
              :disabled="formValue.period != 'd'"
            />
            <span
              v-if="formValue.period == 'd' && !isValidTime"
              class="text-danger text-sm-italic"
              >{{ $t('global.validation.required') }}</span
            >
          </b-col>
        </b-row>
        <b-row class="align-items-center my-3">
          <b-col md="3">
            <b-form-radio
              v-model="formValue.period"
              value="w"
              :disabled="this.report.key === 'offline-vehicle'"
            >
              <label class="radio-label">{{ $t('report.object.everyWeek') }}</label>
            </b-form-radio>
          </b-col>
          <b-col md="3">
            <b-form-group
              label-for="period"
              :invalid-feedback="invalidNoOfDay"
              :state="stateNoOfDay"
            >
              <multiselect
                id="period"
                v-model="formValue.noOfDay"
                :options="dayOptions"
                track-by="value"
                label="text"
                :placeholder="$t('report.action.selectDate')"
                openDirection="bottom"
                :disabled="formValue.period != 'w'"
              />
            </b-form-group>
          </b-col>
          <b-col md="1">
            <div class="float-end">{{ $t('global.time.hours') }}</div>
          </b-col>
          <b-col md="3">
            <date-picker
              v-model="formValue.timeWeek"
              format="HH:mm"
              value-type="format"
              type="time"
              placeholder="HH:mm"
              :disabled="formValue.period != 'w'"
            />
            <span
              v-if="formValue.period == 'w' && !isValidTime"
              class="text-danger text-sm-italic"
              >{{ $t('global.validation.required') }}</span
            >
          </b-col>
        </b-row>
        <b-row class="align-items-center my-3" v-if="haveMonthlyOption">
          <b-col md="3">
            <b-form-radio
              v-model="formValue.period"
              value="m"
              :disabled="this.report.key === 'offline-vehicle'"
            >
              <label class="radio-label">{{ $t('report.object.everyMonth') }}</label>
            </b-form-radio>
          </b-col>
          <b-col md="3">
            <b-form-group
              label-for="period"
              :invalid-feedback="invalidNoOfDate"
              :state="stateNoOfDate"
            >
              <multiselect
                id="period"
                v-model="formValue.noOfDate"
                :options="dateOptions"
                :placeholder="$t('global.action.selectDate')"
                openDirection="bottom"
                :disabled="formValue.period != 'm'"
              />
            </b-form-group>
          </b-col>
          <b-col lg="1" sm="1">
            <div class="float-end">{{ $t('global.time.hours') }}</div>
          </b-col>
          <b-col md="3">
            <date-picker
              v-model="formValue.timeMonth"
              format="HH:mm"
              value-type="format"
              type="time"
              placeholder="HH:mm"
              :disabled="formValue.period != 'm'"
            />
            <span
              v-if="formValue.period == 'm' && !isValidTime"
              class="text-danger text-sm-italic"
              >{{ $t('global.validation.required') }}</span
            >
          </b-col>
        </b-row>
        <b-row class="my-2 align-items-center">
          <b-col md="3">
            <span class="mt-2">{{ $t('report.object.deliveryPeriod') }}</span>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>
                    {{ $t('dashboard.scheduler.form.startDate') }}
                    <i class="text-danger">*</i>
                  </label>
                </div>
              </template>
              <date-picker
                v-model="formValue.startDate"
                format="DD MMM YYYY"
                value-type="YYYY-MM-DD"
                :lang="datePickerLang"
                type="date-picker"
                :placeholder="$t('global.action.selectDate')"
                validation="required"
                :disabled-date="disabledRangeStart"
                :class="{ 'is-invalid': !stateStartDate }"
              />
              <div v-if="!stateStartDate" class="invalid-feedback">
                <span>
                  {{ $t('dashboard.scheduler.form.startDate') }}
                  {{ $t('global.validation.required') }}
                </span>
              </div>
            </b-form-group>
          </b-col>
          <b-col md="1">
            <div class="d-flex justify-content-center align-self-center">-</div>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>{{ $t('dashboard.scheduler.form.endDate') }}</label>
                </div>
              </template>
              <date-picker
                v-model="formValue.endDate"
                format="DD MMM YYYY"
                value-type="YYYY-MM-DD"
                :lang="datePickerLang"
                type="date-picker"
                :placeholder="$t('global.action.selectDate')"
                validation="required"
                :disabled-date="disabledRangeEnd"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="align-items-center my-3" ref="descRef">
          <McAlert v-if="alertDesc" variant="info" :text="alertDesc" withIcon />
        </b-row>
      </div>
      <template #modal-footer>
        <McButton @click="showModal = false" variant="secondary" :text="$t('global.cancel')" />
        <McButton
          :disabled="isInvalidContact"
          @click="submitForm"
          variant="primary"
          :text="$t('global.action.send')"
          icon="icon-outline_paper-plane"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import datePickerLang from '@/helpers/date-picker-lang'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { CREATE_REPORT_SCHEDULER, EXPORT_GET_CONTACTS_REPORT } from '@/services/api/report/report'
import moment from 'moment'
import Multiselect from 'vue-multiselect'
import { mapGetters } from 'vuex'
import { mapCsv, mapPeriode31DaysLeft } from '../lib/reportKeyLists.js'
import Contact from './Contact'

export default {
  components: {
    Contact,
    McButton,
    Multiselect,
    McAlert,
  },
  mixins: [Helper, MixPanel],
  props: {
    report: {
      type: Object,
      required: true,
    },
    filter: {
      type: Object,
    },
    dropdownItems: {
      type: Array,
      required: true,
    },
    labelGraph: {
      type: Object,
    },
    allColumn: {
      type: Array,
    },
  },

  data() {
    return {
      countryCode: '+62',
      hasData: true,
      render: false,
      showModal: false,
      employeesList: [],
      formValue: {
        employeesList: [],
        downloadAs: null,
        isSchedule: false,
        scheduleName: '',
        period: 'd',
        noOfDay: { value: 0, text: this.$t('global.days.sunday') },
        time: '17:00',
        timeWeek: '17:00',
        noOfDate: 1,
        timeMonth: '17:00',
        startDate: moment().format('YYYY-MM-DD'),
        endDate: '',
      },
      isSubmit: false,
      lastContactId: 0,
      reportKeyWeekly: ['alert', 'geolocation', 'summary-stopped'],
      reportKeyWeeklyAndOneVehicle: ['detail-history-vehicle', 'fuel'],
      reportKeyWeeklyOrOneVehicle: ['history-vehicle'],
      reportKeyOneVehicle: ['temperature'],
      reportKeyUseChart: ['fuel', 'temperature'],
    }
  },

  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      hasAccess: 'identity/hasAccess',
    }),
    datePickerLang() {
      return datePickerLang()
    },
    stateDownloadAs() {
      return this.formValue.downloadAs?.value
    },
    stateStartDate() {
      return this.formValue.startDate || !this.isSubmit
    },
    haveMonthlyOption() {
      return (
        mapPeriode31DaysLeft.includes(this.report.key) ||
        mapPeriode31DaysLeft.includes(this.report.reportKey) ||
        ((mapCsv.includes(this.report.key) || mapCsv.includes(this.report.reportKey)) &&
          this.hasCsvScope) ||
        this.checkReportKey('cost-analysis') ||
        this.checkReportKey('history-vehicle')
      )
    },
    hasCsvScope() {
      return this.report.downloadOptions?.includes('csv') && this.hasAccess(['csv_report:read'])
    },
    alertDesc() {
      let time = this.formValue.time
      if (this.formValue.period === 'w') time = this.formValue.timeWeek
      if (this.formValue.period === 'm') time = this.formValue.timeMonth

      const day = this.dayOptions.find((day) => day.value == this.formValue.noOfDay.value)
      let result = ''
      if (this.formValue.period === 'd' && this.formValue.time && this.formValue.startDate) {
        result = this.$t('report.text.scheduleDayInfo', {
          time: this.formValue.time,
          startTime: this.momentDateLongMonth(this.formValue.startDate),
        })
      } else if (
        this.formValue.period === 'w' &&
        day.text &&
        this.formValue.timeWeek &&
        this.formValue.startDate
      ) {
        result = this.$t('report.text.scheduleWeekInfo', {
          day: day.text,
          time: this.formValue.timeWeek,
          startTime: this.momentDateLongMonth(this.formValue.startDate),
        })
      } else if (
        this.formValue.period === 'm' &&
        this.formValue.noOfDate &&
        this.formValue.timeMonth &&
        this.formValue.startDate
      ) {
        result = this.$t('report.text.scheduleMonthInfo', {
          date: this.formValue.noOfDate,
          time: this.formValue.timeMonth,
          startTime: this.momentDateLongMonth(this.formValue.startDate),
        })
      }

      if (this.formValue.startDate && this.formValue.endDate) {
        result += ` ${this.$t('report.text.scheduleDateInfoEnd', {
          endDate: this.momentDateLongMonth(this.formValue.endDate),
        })}`
      }
      return result
    },
    isInvalidContact() {
      return (
        this.formValue.employeesList.length <= 0 ||
        this.formValue.employeesList.every((item) => !item.phoneNumber && !item.email)
      )
    },
    invalidNoOfDate() {
      return `${this.$t('report.object.dateSelection')} ${this.$t('global.validation.required')}`
    },
    stateNoOfDate() {
      return (
        !this.formValue.isSchedule ||
        !this.isSubmit ||
        this.formValue.period !== 'm' ||
        (this.formValue.period === 'm' && this.formValue.noOfDate !== '')
      )
    },
    dateOptions() {
      const result = []
      for (let i = 1; i <= 31; i++) {
        result.push(i)
      }
      return result
    },
    invalidSchedulerName() {
      return `${this.$t('report.title.scheduleName')} ${this.$t('global.validation.required')}`
    },
    stateSchedulerName() {
      return !this.formValue.isSchedule || !this.isSubmit || this.formValue.scheduleName.length > 0
    },
    invalidNoOfDay() {
      return `${this.$t('report.object.daySelection')} ${this.$t('global.validation.required')}`
    },
    stateNoOfDay() {
      return (
        !this.formValue.isSchedule ||
        !this.isSubmit ||
        this.formValue.period !== 'w' ||
        (this.formValue.period === 'w' && this.formValue.noOfDay !== '')
      )
    },
    isValidTime() {
      if (this.formValue.period === 'd' && this.formValue.time) {
        return true
      } else if (this.formValue.period === 'w' && this.formValue.timeWeek) {
        return true
      } else if (this.formValue.period === 'm' && this.formValue.timeMonth) {
        return true
      } else {
        return false
      }
    },
    dayOptions() {
      return [
        { value: 0, text: this.$t('global.days.sunday') },
        { value: 1, text: this.$t('global.days.monday') },
        { value: 2, text: this.$t('global.days.tuesday') },
        { value: 3, text: this.$t('global.days.wednesday') },
        { value: 4, text: this.$t('global.days.thursday') },
        { value: 5, text: this.$t('global.days.friday') },
        { value: 6, text: this.$t('global.days.saturday') },
      ]
    },
    reportWeeklyValidate() {
      return this.reportKeyWeekly.includes(this.report.key) && this.formValue.period != 'm'
    },
    reportOnlyVehicleAndWeeklyValidate() {
      return (
        this.reportKeyWeeklyAndOneVehicle.includes(this.report.key) &&
        this.formValue.period != 'm' &&
        (!this.filter?.vehicleId ||
          (Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId?.length <= 1) ||
          (!Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId))
      )
    },
    reportOnlyVehicleValidate() {
      return (
        this.reportKeyOneVehicle.includes(this.report.key) &&
        (!this.filter?.vehicleId ||
          (Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId?.length <= 1) ||
          (!Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId))
      )
    },
    summaryDayValidate() {
      return (
        this.report.key === 'summary-day' &&
        (this.formValue.period != 'm' ||
          (this.formValue.period === 'm' &&
            (!this.filter?.vehicleId ||
              (Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId?.length <= 100))))
      )
    },
    summaryDayDriverValidate() {
      return (
        this.report.key === 'summary-day-driver' &&
        (this.formValue.period != 'm' ||
          (this.formValue.period === 'm' &&
            (!this.filter?.driverId ||
              (Array.isArray(this.filter?.driverId) && this.filter?.driverId?.length <= 100))))
      )
    },
    reportKeyWeeklyOrOneVehicleValidate() {
      return (
        this.reportKeyWeeklyOrOneVehicle.includes(this.report.key) &&
        (this.formValue.period != 'm' ||
          (this.formValue.period === 'm' &&
            (!this.filter?.vehicleId ||
              (Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId?.length <= 1) ||
              (!Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId))))
      )
    },
    chartDocValidate() {
      return (
        this.reportKeyUseChart.includes(this.report.key) &&
        this.formValue.period != 'm' &&
        (!this.filter?.vehicleId ||
          (Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId?.length <= 1) ||
          (!Array.isArray(this.filter?.vehicleId) && this.filter?.vehicleId))
      )
    },
    downloadOptions() {
      let result = []
      if (this.formValue.isSchedule) {
        if (
          (!this.reportKeyWeekly.includes(this.report.key) &&
            !this.reportKeyWeeklyAndOneVehicle.includes(this.report.key) &&
            !this.reportKeyOneVehicle.includes(this.report.key) &&
            !['summary-day', 'summary-day-driver'].includes(this.report.key) &&
            !this.reportKeyWeeklyOrOneVehicle.includes(this.report.key)) ||
          this.reportWeeklyValidate ||
          this.reportOnlyVehicleAndWeeklyValidate ||
          this.reportOnlyVehicleValidate ||
          this.summaryDayValidate ||
          this.summaryDayDriverValidate ||
          this.reportKeyWeeklyOrOneVehicleValidate
        ) {
          result.push(this.generateDownloadOptions('excel'), this.generateDownloadOptions('pdf'))
        }
        if (this.hasCsvScope) {
          result.push(this.generateDownloadOptions('csv'))
        }
        if (this.chartDocValidate) {
          result.push(this.generateDownloadOptions('graph'))
        }
      } else {
        result = this.dropdownItems.reduce((cum, curr) => {
          const result = this.generateDownloadOptions(curr.name)
          if (result) cum.push(result)
          return cum
        }, [])
      }

      return result
    },
    fields() {
      return [
        {
          name: 'user',
          key: 'user.label',
          label: this.$t('notification.setting.emailNotification.recipientName'),
          placeholder: this.$t('notification.setting.emailNotification.recipientName'),
          isEditable: true,
          type: 'select-resourceful-v2',
          optionsGetter: this.resourcefulQuery.employees,
          trackBy: 'id',
          optionLabelFn: ({ item }) => item.label,
          multiple: false,
          taggable: true,
          customColumn: true,
          validationBehavior: 'blur',
          errorBehavior: 'blur',
        },
        {
          name: 'phoneNumber',
          key: 'phoneNumber',
          label: this.$t('notification.setting.emailNotification.whatsappNo'),
          placeholder: this.$t('notification.setting.emailNotification.whatsappNo'),
          type: 'phone-number',
          isEditable: true,
          thStyle: 'width: 300px',
          validation: [['isPhoneNumber', this.countryCode]],
          customColumn: true,
          validationBehavior: 'blur',
          errorBehavior: 'blur',
          disabled: this.hasWhatsapp,
        },
        {
          name: 'email',
          key: 'email',
          label: this.$t('notification.setting.emailNotification.email'),
          placeholder: this.$t('notification.setting.emailNotification.email'),
          isEditable: true,
          type: 'text',
          thStyle: 'width: 400px',
          validation: 'optional|email',
          validationBehavior: 'blur',
          errorBehavior: 'blur',
        },
      ]
    },

    resourcefulQuery() {
      return {
        employees: async (opts) => {
          let result = {}
          let employeeList = this.employeesList

          if (opts?.page === 1) employeeList = []

          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: EXPORT_GET_CONTACTS_REPORT,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })
          const self = this

          const employees = res.data.contactWithUsers.contactWithUsers
          employees.data.forEach((item, index) => {
            employees.data[index].id = self.lastContactId + index + 1
          })
          self.lastContactId = employees.data.length
            ? employees.data[employees.data.length - 1].id
            : self.lastContactId

          employees.data.map((item) => employeeList.push(item))

          self.employeesList = employeeList

          result = {
            metadata: {
              total_count: employees.pagination.total,
              page: employees.pagination.page,
            },
            data: {
              paginated_result: employees.data,
            },
          }

          return result
        },
      }
    },

    hasWhatsapp() {
      const disabled = this.hasAccessAll(['whatsapp-integration:read'])
      return !disabled
    },

    filterReport() {
      const result = {
        ...this.filter,
        fileType: [this.formValue.downloadAs.actualValue],
      }
      if (this.formValue.downloadAs.actualValue == 'GP') {
        result.labels = this.labelGraph.labels()

        if (this.allColumn) {
          result.column = this.allColumn.filter(
            (field) => field != 'address' && field !== 'geofence'
          )
        }
      }
      return result
    },
  },

  watch: {
    countryCode: {
      handler(value) {
        if (value) {
          if (value.includes('+')) this.countryCode = value
          else this.countryCode = `+${value}`
        } else this.countryCode = '+62'
      },
    },
    downloadOptions: {
      handler(val) {
        if (
          !val.map((item) => item.actualValue).includes(this.formValue.downloadAs?.actualValue) &&
          this.formValue.downloadAs
        )
          this.formValue.downloadAs = null
      },
      deep: true,
    },
  },

  methods: {
    disabledRangeStart: function (date) {
      if (this.formValue.endDate) {
        return new Date(this.momentYmD(date)).getTime() > new Date(this.formValue.endDate).getTime()
      }
      return false
    },
    disabledRangeEnd: function (date) {
      if (this.formValue.startDate) {
        return (
          new Date(this.momentYmD(date)).getTime() < new Date(this.formValue.startDate).getTime()
        )
      }
      return false
    },
    toggleSchedule(checked) {
      if (checked) {
        setTimeout(() => {
          this.$refs.descRef.scrollIntoView({
            behavior: 'smooth',
          })
        }, 100)
      }
    },
    generateDownloadOptions(type) {
      if (type === 'pdf') {
        return {
          value: 'pdf',
          label: this.$t('global.object.pdf'),
          icon: 'icon-fill_pdf',
          actualValue: 'TP',
        }
      } else if (type === 'excel') {
        return {
          value: 'xlsx',
          label: this.$t('global.object.excel'),
          icon: 'icon-fill_excel',
          actualValue: 'TE',
        }
      } else if (type === 'csv') {
        return {
          value: 'csv',
          label: this.$t('global.object.csv'),
          icon: 'icon-fill_excel',
          actualValue: 'TC',
        }
      } else if (type === 'graph') {
        return {
          value: 'graph',
          label: this.$t('global.object.graphicPdf'),
          icon: 'icon-fill_pdf',
          actualValue: 'GP',
        }
      }
      return null
    },
    checkReportKey(key) {
      return this.report.key === key || this.report.reportKey === key
    },
    titleMixpanel(extension) {
      let titleMixpanel = ''
      if (extension === 'pdf') {
        titleMixpanel = 'PDF'
      } else if (extension === 'xlsx') {
        titleMixpanel = 'Excel'
      } else if (extension === 'csv') {
        titleMixpanel = 'CSV'
      } else if (extension === 'graph') {
        titleMixpanel = 'Chart PDF'
      }
      return titleMixpanel
    },
    async submitForm() {
      this.isSubmit = true
      if (
        this.stateNoOfDate &&
        this.stateSchedulerName &&
        this.stateNoOfDay &&
        this.stateDownloadAs &&
        this.isValidTime &&
        this.stateStartDate &&
        !this.isInvalidContact
      ) {
        this.isSubmit = false
        this.formValue.employeesList = this.formValue.employeesList.filter(
          (item) => item.id !== undefined
        )
        const contactFilter = this.generateContactFilter(this.formValue.employeesList)
        if (this.formValue.isSchedule) {
          let noOfDay = null
          if (this.formValue.period === 'w') noOfDay = this.formValue.noOfDay.value
          else if (this.formValue.period === 'm') noOfDay = this.formValue.noOfDate

          let time = this.formValue.time
          if (this.formValue.period === 'w') time = this.formValue.timeWeek
          else if (this.formValue.period === 'm') time = this.formValue.timeMonth

          this.track(
            `Scheduling Share ${this.report.event} - ${this.titleMixpanel(
              this.formValue.downloadAs.value
            )}`
          )
          const filter = this.filterReport
            ? JSON.stringify(this.modifyFilterScheduler(this.filterReport))
            : undefined
          const input = {
            scheduleName: this.formValue.scheduleName,
            reportName: this.report.labelName ?? this.$t(this.report.label, 'id'),
            reportKey: this.report.reportKey ?? this.report.key,
            period: this.formValue.period,
            noOfDay: noOfDay,
            time: moment(time, 'HH:mm').format('HH:mm:ss'),
            startTime: moment(this.formValue.startDate).toISOString(),
            endTime: this.formValue.endDate ? moment(this.formValue.endDate).toISOString() : null,
            filter,
            contactRecipients: contactFilter,
            isSavedReport: this.report.labelName && this.report.reportKey ? true : false,
          }
          await this.$apollo
            .mutate({
              mutation: CREATE_REPORT_SCHEDULER,
              variables: {
                input: input,
              },
            })
            .then((response) => {
              if (!response.data.createReportScheduler.isSuccessful) {
                this.$mcToast.error(response.data.createReportScheduler.message)
              }
            })
        }
        this.$emit('doShare', this.formValue.downloadAs.value, contactFilter)
        this.showModal = false
      }
    },
    generateContactFilter(contact) {
      if (!contact) return []
      const result = contact.map((item) => {
        const label = item.user.fullname ?? item.user.label
        return {
          label: label ? label : null,
          userId: item.user.userId === 9999999 ? null : item.user.userId,
          phoneNumber: item.phoneNumber ? item.phoneNumber : null,
          email: item.email ? item.email : null,
          contactId: item.contactId,
        }
      })
      return result
    },
    modifyFilterScheduler(filter) {
      let motionStatus = []
      if (filter.motionStatus) {
        if (filter.motionStatus.includes('assisted')) {
          motionStatus.push('A')
        }
        if (filter.motionStatus.includes('moving')) {
          motionStatus.push('M')
        }

        filter.motionStatus = motionStatus
      }
      return filter
    },
    updateData(data) {
      this.formValue.employeesList = data
    },
    updateCountryCode(value) {
      this.countryCode = value
    },

    addTag(obj) {
      this.employeesList.push(obj)
    },
  },
}
</script>
